import { useNavigate } from "react-router-dom";
import { usePortal, type ClientAdminSummary, type ClientSummary } from "@app/portal-sdk";
import { Alert, AppCard, Box, Button, EmptyState, PageHeader, Stack } from "@app/ui-kit";
import { LoadingScreen } from "../components/LoadingScreen";
import { useApiQuery } from "../lib/useApiQuery";

/** Pick a client portal. Members see their clients; BKDP admins see every client. */
export const ClientSelect = () => {
  const navigate = useNavigate();
  const { api, isBkdpAdmin, memberships } = usePortal();

  const admin = useApiQuery<ClientAdminSummary[]>(
    () => (isBkdpAdmin ? api.request<ClientAdminSummary[]>("/admin/clients") : Promise.resolve([])),
    [api, isBkdpAdmin]
  );

  const clients: ClientSummary[] = isBkdpAdmin
    ? admin.data ?? []
    : memberships.filter((m) => m.status === "active").map((m) => m.client);

  if (isBkdpAdmin && admin.loading) {
    return <LoadingScreen />;
  }

  return (
    <Stack spacing={5}>
      <PageHeader
        eyebrow="Client Portal"
        title="Choose a client"
        subtitle={
          isBkdpAdmin
            ? "Open any client portal as BKDP."
            : "You belong to more than one client. Pick the one you want to open."
        }
      />
      {admin.error && <Alert severity="error">{admin.error}</Alert>}
      {clients.length === 0 ? (
        <EmptyState eyebrow="No Clients" title="Nothing to open yet" />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3
          }}
        >
          {clients.map((client) => (
            <AppCard
              key={client.id}
              eyebrow={client.status === "active" ? "Client" : "Suspended"}
              title={client.name}
              subtitle={client.brand.tagline ?? `/c/${client.slug}`}
              action={
                <Button size="small" variant="outlined" onClick={() => navigate(`/c/${client.slug}`)}>
                  Open
                </Button>
              }
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};
